import { computed, toValue, type MaybeRefOrGetter } from "vue";
import { useSeoMeta } from "@unhead/vue";

import { appUrl } from "@/composables/useShare";
import { toMetaDescription } from "@/utils/seo-text";

/*****************************************************************************
 * Per-page title and link-preview meta (rank 49).
 *
 * Every tab, bookmark, history entry and search result read 'True Market
 * Insiders': @unhead/vue was installed but nothing wrote to it. useAppHead
 * (App.vue) holds the site-wide defaults, the route's meta.title, robots and
 * the canonical link; a page with a data-driven name adds one usePageSeo call.
 * Its entry is newer than the app's, so its tags win while it is mounted and
 * the defaults come back when it unmounts.
 *****************************************************************************/

// The text rules are shared with the build's prerendered heads.
export {
    DEFAULT_DESCRIPTION,
    DEFAULT_OG_IMAGE_PATH,
    DESCRIPTION_MAX_LENGTH,
    SITE_NAME,
    siteTitle,
    toMetaDescription
} from "@/utils/seo-text";

type MaybeText = string | null | undefined;

/** An image link as unfurlers need it: absolute, https for a protocol-relative CMS asset. Empty is undefined. */
export function absoluteImageUrl( image: MaybeText ): string | undefined {
    const trimmed = image?.trim();
    if ( !trimmed ) return undefined;
    if ( /^https?:\/\//i.test( trimmed ) ) return trimmed;
    if ( trimmed.startsWith( '//' ) ) return `https:${ trimmed }`;
    return appUrl( trimmed );
}

export interface PageSeoOptions {
    /** og:type. 'website' (the app default) when omitted. */
    type?: 'article' | 'profile' | 'website';
    /** ISO date of an article's publication. */
    publishedTime?: MaybeRefOrGetter< MaybeText >;
    /** True keeps the page out of search results, e.g. a detail page whose record does not exist. */
    noindex?: MaybeRefOrGetter< boolean >;
}

function textOrUndefined( value: MaybeText ): string | undefined {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
}

/**
 * Names the page in the tab title, search results and link previews. Call once in setup. Every argument may be a
 * ref or getter; an empty value leaves the app's default in place (the route's meta.title, the site description and
 * image), so a page can call this before its data has loaded.
 *
 * @param title the page's own name, without the site name (the title template adds it)
 * @param description plain text, never HTML; it is collapsed and cut to DESCRIPTION_MAX_LENGTH
 * @param image an absolute or site-relative image for link previews
 */
export function usePageSeo(
    title: MaybeRefOrGetter< MaybeText >,
    description?: MaybeRefOrGetter< MaybeText >,
    image?: MaybeRefOrGetter< MaybeText >,
    options: PageSeoOptions = {}
): void {
    const pageTitle = computed( () => textOrUndefined( toValue( title ) ) );
    const pageDescription = computed( () => toMetaDescription( toValue( description ) ) );
    const pageImage = computed( () => absoluteImageUrl( toValue( image ) ) );
    const publishedTime = computed( () => textOrUndefined( toValue( options.publishedTime ) ) );

    useSeoMeta( {
        title: pageTitle,
        ogTitle: pageTitle,
        twitterTitle: pageTitle,
        description: pageDescription,
        ogDescription: pageDescription,
        twitterDescription: pageDescription,
        ogImage: pageImage,
        twitterImage: pageImage,
        ogType: options.type,
        articlePublishedTime: publishedTime,
        robots: computed( () => toValue( options.noindex ) ? 'noindex' : undefined )
    } );
}
